import { Component, forwardRef, OnDestroy, OnInit, input } from '@angular/core';
import { SchemaTranslations } from '../../../../../shared/models/schema-translations';
import { Schema } from '../../../../../core/_models/schema';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { AbstractControlValueAccessor } from '../../../../../shared/abstract_control_value_accessor.abstract';
import { Subscription } from 'rxjs';

import { PropertyIsFieldRequiredPipe } from '@app/+profile-form/components/properties-form/property-is-field-required.pipe';
import { ShowPropertyArrayHintPipe } from '@app/+profile-form/components/properties-form/property-array/show-property-array-hint.pipe';
import { HintComponent } from '@app/+profile-form/components/properties-form/property-array/hint/hint.component';
import { PropertyArrayHintTextPipe } from '@app/+profile-form/components/properties-form/property-array/property-array-hint-text.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { PropertyGetFieldsPipe } from '@app/+profile-form/components/properties-form/property-array/property-get-fields.pipe';
import { PropertyGetEnumValuesPipe } from '@app/+profile-form/components/properties-form/property-array/property-get-enum-values.pipe';
import { PropertyHasNumericValuesPipe } from '@app/+profile-form/components/properties-form/property-array/property-has-numeric-values.pipe';

@Component({
  selector: 'app-property-array-dynamic',
  templateUrl: './property-array-dynamic.component.html',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PropertyArrayDynamicComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => PropertyArrayDynamicComponent), multi: true },
  ],
  imports: [
    PropertyIsFieldRequiredPipe,
    ShowPropertyArrayHintPipe,
    HintComponent,
    PropertyArrayHintTextPipe,
    TranslatePipe,
    PropertyGetFieldsPipe,
    ReactiveFormsModule,
    PropertyGetEnumValuesPipe,
    PropertyHasNumericValuesPipe
],
})
export class PropertyArrayDynamicComponent extends AbstractControlValueAccessor implements OnInit, OnDestroy, Validator {
  public readonly controlName = input<string>(undefined!);
  public readonly properties = input<Schema>(undefined!);
  public readonly schemaTranslations = input<SchemaTranslations | null>(undefined!);
  public readonly errors = input<any>();
  public formArray = new UntypedFormArray([]);
  private formSub: Subscription;

  public ngOnInit(): void {
    this.formSub = this.formArray.valueChanges.subscribe(() => this.formControlChange());
  }

  public ngOnDestroy(): void {
    this.formSub.unsubscribe();
  }

  public writeValue(obj: any): void {
    if (!obj) { return; }

    obj.forEach((enumValue) => {
      if (!Array.from(this.formArray.controls.values()).includes(enumValue)) { this.addFormControl(enumValue); }
    });

    this.formArray.patchValue(obj);
  }

  public validate(): ValidationErrors | any {
    return this.formArray.errors;
  }

  private formControlChange(): void {
    let values = this.formArray.value;
    values = values.filter((enumValue) => enumValue !== '');

    if (this.properties().hasNumericValues(this.controlName())) { values = values.map((enumValue) => parseInt(enumValue, 10)); }

    this.onTouched();
    this.onChange(values);
  }

  public addFormControl(value = ''): void {
    this.formArray.push(new UntypedFormControl(value));
  }

  public showPlus(): boolean {
    const maxItems = this.properties().getField(this.controlName()).maxItems;

    if (!maxItems) { return true; }

    return this.formArray.length < maxItems;
  }
}
